import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  CircularProgress,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Alert,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Collapse,
  Pagination,
  Stack,
  Checkbox,
  Card,
  CardContent,
  Grid,
  Chip,
  Tooltip,
  Switch,
  FormControlLabel,
  Tabs,
  Tab
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DescriptionIcon from '@mui/icons-material/Description';
import axios from 'axios';
import * as apiService from '../../services/apiService';
import { isAuthenticated } from '../../services/authService';
import DailyExecutionTab from './DailyExecutionTab';

/**
 * RecommendedTestsView component for displaying recommended tests data
 * @returns {JSX.Element} RecommendedTestsView component
 */
const RecommendedTestsView = () => {
  // Log that component is rendering (for debugging)
  console.log('RecommendedTestsView component rendering - this is a public page');
  console.log('Current URL:', window.location.href);
  console.log('Is authenticated:', isAuthenticated());
  
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState('desc'); // Default to descending (newest first)
  const [orderBy, setOrderBy] = useState('dateTime'); // Default sort by dateTime
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });
  
  // Modal states
  const [codeChangesModalOpen, setCodeChangesModalOpen] = useState(false);
  const [impactedTestsModalOpen, setImpactedTestsModalOpen] = useState(false);
  const [selectedCodeChanges, setSelectedCodeChanges] = useState([]);
  const [selectedImpactedTests, setSelectedImpactedTests] = useState([]);
  const [selectedTraceability, setSelectedTraceability] = useState([]);
  const [loadingMethods, setLoadingMethods] = useState(false);
  const [methodsError, setMethodsError] = useState(null);
  const [githubCodeChanges, setGithubCodeChanges] = useState(null);
  const [loadingGithubCodeChanges, setLoadingGithubCodeChanges] = useState(false);
  const [githubCodeChangesError, setGithubCodeChangesError] = useState(null);
  const [modalTitle, setModalTitle] = useState('');
  const [testSearchQuery, setTestSearchQuery] = useState('');
  const [methodSearchQuery, setMethodSearchQuery] = useState('');
  const [methodSearchInput, setMethodSearchInput] = useState('');
  const [expandedClasses, setExpandedClasses] = useState(new Set());
  const [expandedMethods, setExpandedMethods] = useState(new Set());
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'card'
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [selectedPRForLogs, setSelectedPRForLogs] = useState(null);
  const [showOnlyWithTests, setShowOnlyWithTests] = useState(false);
  // Date groups: set of date keys that are expanded (default all expanded)
  const [expandedDateGroups, setExpandedDateGroups] = useState(() => new Set());
  // Cleanup data dialog
  const [cleanupDialogOpen, setCleanupDialogOpen] = useState(false);
  const [cleanupImpactedMethods, setCleanupImpactedMethods] = useState(false);
  const [cleanupImpactedTests, setCleanupImpactedTests] = useState(false);
  const [cleanupPullRequests, setCleanupPullRequests] = useState(false);
  const [cleanupInProgress, setCleanupInProgress] = useState(false);
  const [cleanupError, setCleanupError] = useState(null);
  const [cleanupOlderThan, setCleanupOlderThan] = useState('');
  const [subTab, setSubTab] = useState(0); // 0 = Impacted Tests, 1 = Daily Execution
  
  // Check if user is authenticated
  const userIsAuthenticated = isAuthenticated();

  useEffect(() => {
    fetchRecommendedTests();
  }, [page]);

  const fetchRecommendedTests = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching recommended tests from public API...');
      const response = await apiService.get('/api/recommended-tests/list', {
        page,
        pageSize
      }, 'json', { useCache: false });
      console.log('Recommended tests API response:', response);
      if (response.success) {
        const records = response.data;
        setData(records);
        
        // Update pagination state
        if (response.pagination) {
          setPagination(response.pagination);
        }

        // Fetch methods counts for records that have methods but traceability not loaded
        // Keep this for internal use but don't display in UI
        const recordsNeedingCounts = records.filter(record => 
          record.hasMethods && (!record.traceability || !Array.isArray(record.traceability) || record.traceability.length === 0)
        );

        if (recordsNeedingCounts.length > 0 && userIsAuthenticated) {
          try {
            const countsResponse = await axios.post('/api/recommended-tests/methods/counts', {
              records: recordsNeedingCounts.map(record => ({
                groupId: record.groupId,
                appId: record.appId,
                buildVersion: record.buildVersion,
                baselineBuildVersion: record.baselineBuildVersion
              }))
            });

            if (countsResponse.data.success) {
              // Create a map of counts by record key
              const countsMap = new Map();
              countsResponse.data.data.forEach(item => {
                const key = `${item.groupId}::${item.appId}::${item.buildVersion}::${item.baselineBuildVersion}`;
                countsMap.set(key, item.count);
              });

              // Update records with counts (keep for internal use, not displayed in UI)
              records.forEach(record => {
                const key = `${record.groupId}::${record.appId}::${record.buildVersion}::${record.baselineBuildVersion}`;
                const count = countsMap.get(key);
                if (count !== undefined) {
                  record.methodsCount = count;
                }
              });
            }
          } catch (countsError) {
            console.error('Error fetching methods counts:', countsError);
            // Continue even if counts fetch fails
          }
        }

        // Fetch GitHub code changes for each PR (Files Changed column - public, no login required)
        const codeChangesPromises = records.map(async (record) => {
          const prDetails = record.prDetails;
          const prNumber = prDetails?.prNumber || record.prNumber;
          const appId = record.appId;

          if (!prNumber || !appId) {
            return { recordIndex: records.indexOf(record), codeChanges: null };
          }

          try {
            const codeChangesResponse = await apiService.getPRCodeChanges(prNumber, appId);
            if (codeChangesResponse.success && codeChangesResponse.data) {
              return { recordIndex: records.indexOf(record), codeChanges: codeChangesResponse.data };
            }
          } catch (error) {
            // 404 is expected if code changes haven't been fetched yet - don't log as error
            if (error.response?.status !== 404) {
              console.error(`Error fetching code changes for PR ${prNumber}:`, error);
            }
            // Continue even if code changes fetch fails
          }
          return { recordIndex: records.indexOf(record), codeChanges: null };
        });

        const codeChangesResults = await Promise.all(codeChangesPromises);
        
        // Update records with code changes data
        const updatedRecords = records.map((record, index) => {
          const result = codeChangesResults.find(r => r.recordIndex === index);
          if (result && result.codeChanges) {
            return {
              ...record,
              githubCodeChanges: result.codeChanges
            };
          }
          return record;
        });

        setData(updatedRecords);
      } else {
        setError('Failed to fetch recommended tests');
      }
      } catch (err) {
      console.error('Error fetching recommended tests:', err);
      // Don't redirect on 401 - just show error message
      const errorMessage = err.response?.data?.message || err.message || 'Error fetching recommended tests';
      setError(errorMessage);
      
      // Log detailed error for debugging
      if (err.response) {
        console.error('API Error Response:', {
          status: err.response.status,
          statusText: err.response.statusText,
          data: err.response.data
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle sorting
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Helper function to get unique methods count (same as in render)
  // Helper function to get status color
  const getStatusColor = (status) => {
    const statusLower = (status || '').toLowerCase();
    switch (statusLower) {
      case 'executed':
        return '#4caf50'; // Green
      case 'to be executed':
        return '#ff9800'; // Orange
      case 'merged':
        return '#4caf50'; // Green
      case 'closed':
        return '#ff9800'; // Orange
      case 'open':
      case 'opened':
      case 'created':
        return '#2196f3'; // Blue
      case 'updated':
      case 'synchronize':
        return '#9c27b0'; // Purple
      default:
        return '#9e9e9e'; // Gray
    }
  };

  const getStateColor = (state) => {
    const stateLower = (state || '').toLowerCase();
    switch (stateLower) {
      case 'merged':
        return '#4caf50'; // Green
      case 'closed':
        return '#ff9800'; // Orange
      case 'open':
        return '#2196f3'; // Blue
      default:
        return '#9e9e9e'; // Gray
    }
  };

  /**
   * Get short SHA (first 7 characters) for display
   * @param {string} sha - Full commit SHA
   * @returns {string} Short SHA (7 characters) or original if shorter
   */
  const getShortSha = (sha) => {
    if (!sha || sha.length <= 7) return sha;
    return sha.substring(0, 7);
  };

  // Get code changes count from github-code-changes
  const getCodeChangesCount = (row) => {
    if (row.githubCodeChanges) {
      // Use totalChanges if available, otherwise calculate from summary
      if (row.githubCodeChanges.summary?.totalChanges !== undefined) {
        return row.githubCodeChanges.summary.totalChanges;
      }
      // Fallback: calculate from fileChanges array
      if (Array.isArray(row.githubCodeChanges.fileChanges)) {
        return row.githubCodeChanges.fileChanges.reduce((sum, file) => sum + (file.changes || 0), 0);
      }
    }
    return 0;
  };

  // Keep getUniqueMethodsCount for internal use (not displayed in UI)
  const getUniqueMethodsCount = (row) => {
    const traceability = row.traceability;
    const hasMethods = row.hasMethods;
    const methodsCount = row.methodsCount; // Count fetched from API
    
    // If traceability is available, calculate from it
    if (Array.isArray(traceability) && traceability.length > 0) {
      const methodSet = new Set();
      traceability.forEach(test => {
        if (Array.isArray(test.methods)) {
          test.methods.forEach(method => {
            const methodKey = `${method.className || 'unknown'}::${method.methodName || 'unknown'}`;
            methodSet.add(methodKey);
          });
        }
      });
      return methodSet.size;
    }
    
    // If methodsCount was fetched from API, use it
    if (methodsCount !== undefined && methodsCount !== null) {
      return methodsCount;
    }
    
    // If hasMethods is true but no count available yet, return 0 (will show as 0 until loaded)
    if (hasMethods) {
      return 0; // Will be updated when counts are fetched
    }
    
    return 0;
  };

  // Comparator function for sorting
  const descendingComparator = (a, b, orderBy) => {
    // Handle dateTime sorting - use last updated date (updatedAt, lastSyncAt, or dateTime)
    if (orderBy === 'dateTime') {
      // Get last updated date from PR details or fallback to dateTime
      const dateA = a.prDetails?.lastSyncAt 
        ? new Date(a.prDetails.lastSyncAt)
        : (a.updatedAt ? new Date(a.updatedAt) : (a.dateTime ? new Date(a.dateTime) : null));
      const dateB = b.prDetails?.lastSyncAt 
        ? new Date(b.prDetails.lastSyncAt)
        : (b.updatedAt ? new Date(b.updatedAt) : (b.dateTime ? new Date(b.dateTime) : null));
      
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1; // Put null dates at the end
      if (!dateB) return -1; // Put null dates at the end
      
      // Return dateB - dateA for descending (newest first)
      return dateB - dateA;
    }


    if (orderBy === 'impactedTests') {
      const valA = Array.isArray(a.recommendedTests) ? a.recommendedTests.length : 0;
      const valB = Array.isArray(b.recommendedTests) ? b.recommendedTests.length : 0;
      return valA - valB;
    }

    // Handle string sorting
    const valA = a[orderBy] || '';
    const valB = b[orderBy] || '';
    
    if (typeof valA === 'string' && typeof valB === 'string') {
      return valA.localeCompare(valB);
    }
    
    if (valB < valA) return -1;
    if (valB > valA) return 1;
    return 0;
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  // Sort the data
  const sortedData = React.useMemo(() => {
    if (!data || data.length === 0) return [];
    const sorted = [...data].sort(getComparator(order, orderBy));
    
    // Filter to show only PRs with tests if toggle is enabled
    if (showOnlyWithTests) {
      return sorted.filter(row => {
        const impactedTestsCount = Array.isArray(row.recommendedTests) ? row.recommendedTests.length : 0;
        return impactedTestsCount > 0;
      });
    }
    
    return sorted;
  }, [data, order, orderBy, showOnlyWithTests]);

  // Get date key (YYYY-MM-DD) and display label for a row (for grouping)
  const getRowDateKey = (row) => {
    const dateValue = row.prDetails?.lastSyncAt || row.dateTime || row.updatedAt || row.createdAt;
    if (!dateValue) return { key: 'no-date', label: 'No date' };
    const d = new Date(dateValue);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    return { key, label };
  };

  // Group sorted data by date and compute unique test count per group
  const groupedByDate = React.useMemo(() => {
    if (!sortedData || sortedData.length === 0) return [];
    const map = new Map(); // dateKey -> { dateKey, dateLabel, rows, uniqueTestCount }
    sortedData.forEach((row) => {
      const { key, label } = getRowDateKey(row);
      if (!map.has(key)) {
        map.set(key, { dateKey: key, dateLabel: label, rows: [], uniqueTestKeys: new Set() });
      }
      const group = map.get(key);
      group.rows.push(row);
      const tests = Array.isArray(row.recommendedTests) ? row.recommendedTests : [];
      tests.forEach((t) => {
        const ukey = `${t.testPath || ''}::${t.testName || ''}`;
        if (ukey.trim()) group.uniqueTestKeys.add(ukey);
      });
    });
    return Array.from(map.values()).map((g) => ({
      dateKey: g.dateKey,
      dateLabel: g.dateLabel,
      rows: g.rows,
      prCount: g.rows.length,
      uniqueTestCount: g.uniqueTestKeys.size
    }));
  }, [sortedData]);

  // Default state is all collapsed (expandedDateGroups stays empty)

  const toggleDateGroup = (dateKey) => {
    setExpandedDateGroups((prev) => {
      const next = new Set(prev);
      if (next.has(dateKey)) next.delete(dateKey);
      else next.add(dateKey);
      return next;
    });
  };

  const handleOpenCleanupDialog = () => {
    setCleanupError(null);
    setCleanupImpactedMethods(false);
    setCleanupImpactedTests(false);
    setCleanupPullRequests(false);
    setCleanupOlderThan('');
    setCleanupDialogOpen(true);
  };

  const handleRunCleanup = async () => {
    if (!cleanupImpactedMethods && !cleanupImpactedTests && !cleanupPullRequests) {
      setCleanupError('Select at least one option to cleanup.');
      return;
    }
    setCleanupInProgress(true);
    setCleanupError(null);
    try {
      const body = {
        cleanupImpactedMethods: cleanupImpactedMethods || undefined,
        cleanupImpactedTests: cleanupImpactedTests || undefined,
        cleanupPullRequests: cleanupPullRequests || undefined
      };
      if (cleanupOlderThan.trim()) {
        body.olderThan = cleanupOlderThan.trim();
      }
      const response = await apiService.authPost('/api/recommended-tests/cleanup', body);
      if (response.success) {
        setCleanupDialogOpen(false);
        await fetchRecommendedTests();
      } else {
        setCleanupError(response.message || 'Cleanup failed');
      }
    } catch (err) {
      console.error('Cleanup error:', err);
      setCleanupError(err.response?.data?.message || err.message || 'Cleanup failed');
    } finally {
      setCleanupInProgress(false);
    }
  };

  const handleCodeChangesClick = async (row) => {
    // Show GitHub code changes instead of impacted methods
    const prDetails = row.prDetails;
    const prNumber = prDetails?.prNumber || row.prNumber;
    const appId = row.appId;

    if (!prNumber || !appId) {
      setGithubCodeChangesError('PR number or appId not available');
      setCodeChangesModalOpen(true);
      setModalTitle(`Code Changes for PR #${prNumber || 'N/A'}`);
      return;
    }

    // If code changes are already loaded in the row, use them
    if (row.githubCodeChanges) {
      setGithubCodeChanges(row.githubCodeChanges);
      setModalTitle(`Code Changes for PR #${prNumber}`);
      setCodeChangesModalOpen(true);
      setLoadingGithubCodeChanges(false);
      setGithubCodeChangesError(null);
      return;
    }

    // Otherwise, fetch from API (code-changes endpoint is public)
    setLoadingGithubCodeChanges(true);
    setGithubCodeChangesError(null);
    setGithubCodeChanges(null);
    setModalTitle(`Code Changes for PR #${prNumber}`);
    setCodeChangesModalOpen(true);

    try {
      const response = await apiService.getPRCodeChanges(prNumber, appId);
      if (response.success && response.data) {
        setGithubCodeChanges(response.data);
      } else {
        throw new Error(response.message || 'Failed to fetch GitHub code changes');
      }
    } catch (error) {
      console.error('Error fetching GitHub code changes:', error);
      setGithubCodeChangesError(error.message || 'Failed to load GitHub code changes');
    } finally {
      setLoadingGithubCodeChanges(false);
    }
  };

  const handleImpactedTestsClick = (recommendedTests) => {
    setSelectedImpactedTests(recommendedTests || []);
    setModalTitle('Impacted Tests');
    setImpactedTestsModalOpen(true);
  };

  const handleFilesChangedClick = async (row) => {
    const prDetails = row.prDetails;
    const prNumber = prDetails?.prNumber || row.prNumber;
    const appId = row.appId;

    if (!prNumber || !appId) {
      setGithubCodeChangesError('PR number or appId not available');
      return;
    }

    setLoadingGithubCodeChanges(true);
    setGithubCodeChangesError(null);
    setGithubCodeChanges(null);
    setModalTitle('Files Changed');
    setCodeChangesModalOpen(true);

    try {
      const response = await apiService.getPRCodeChanges(prNumber, appId);
      if (response.success && response.data) {
        setGithubCodeChanges(response.data);
      } else {
        throw new Error(response.error || 'Failed to fetch code changes');
      }
    } catch (error) {
      console.error('Error fetching GitHub code changes:', error);
      setGithubCodeChangesError(error.response?.data?.error || error.message || 'Failed to load code changes');
      setGithubCodeChanges(null);
    } finally {
      setLoadingGithubCodeChanges(false);
    }
  };

  const handleCloseCodeChangesModal = () => {
    setCodeChangesModalOpen(false);
    setSelectedTraceability([]);
    setLoadingMethods(false);
    setMethodsError(null);
    setExpandedClasses(new Set());
    setExpandedMethods(new Set());
    setMethodSearchQuery('');
    setMethodSearchInput('');
    setGithubCodeChanges(null);
    setLoadingGithubCodeChanges(false);
    setGithubCodeChangesError(null);
  };

  const handleMethodSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      setMethodSearchQuery(methodSearchInput);
    }
  };

  const toggleClass = (className) => {
    const newExpanded = new Set(expandedClasses);
    if (newExpanded.has(className)) {
      newExpanded.delete(className);
    } else {
      newExpanded.add(className);
    }
    setExpandedClasses(newExpanded);
  };

  const toggleMethod = (methodKey) => {
    const newExpanded = new Set(expandedMethods);
    if (newExpanded.has(methodKey)) {
      newExpanded.delete(methodKey);
    } else {
      newExpanded.add(methodKey);
    }
    setExpandedMethods(newExpanded);
  };

  // Build optimized tree structure: Class -> Method -> Test (memoized)
  const methodTree = React.useMemo(() => {
    const tree = {};
    
    if (!Array.isArray(selectedTraceability)) return tree;
    
    // Use Map for faster lookups during building
    const classMap = new Map();
    
    selectedTraceability.forEach(test => {
      if (!Array.isArray(test.methods)) return;
      
      const testName = test.testName || 'Unknown Test';
      
      test.methods.forEach(method => {
        const className = method.className || 'Unknown Class';
        const methodName = method.methodName || 'Unknown Method';
        
        if (!classMap.has(className)) {
          classMap.set(className, new Map());
        }
        
        const methodMap = classMap.get(className);
        if (!methodMap.has(methodName)) {
          methodMap.set(methodName, new Set());
        }
        
        methodMap.get(methodName).add(testName);
      });
    });
    
    // Convert Maps to plain objects for rendering
    classMap.forEach((methodMap, className) => {
      tree[className] = {};
      methodMap.forEach((testSet, methodName) => {
        tree[className][methodName] = Array.from(testSet).sort();
      });
    });
    
    return tree;
  }, [selectedTraceability]);

  // Filter tree based on search query
  const filteredMethodTree = React.useMemo(() => {
    if (!methodSearchQuery.trim()) {
      return methodTree;
    }
    
    const query = methodSearchQuery.toLowerCase();
    const filtered = {};
    
    Object.keys(methodTree).forEach(className => {
      const classNameLower = className.toLowerCase();
      const methods = methodTree[className];
      const filteredMethods = {};
      
      Object.keys(methods).forEach(methodName => {
        const methodNameLower = methodName.toLowerCase();
        const tests = methods[methodName];
        
        // Check if method name matches
        const methodMatches = methodNameLower.includes(query);
        
        // Check if any test name matches
        const matchingTests = tests.filter(testName => 
          testName.toLowerCase().includes(query)
        );
        
        // Include method if method name matches or has matching tests
        if (methodMatches || matchingTests.length > 0) {
          filteredMethods[methodName] = methodMatches ? tests : matchingTests;
        }
      });
      
      // Include class if class name matches or has matching methods
      if (classNameLower.includes(query) || Object.keys(filteredMethods).length > 0) {
        filtered[className] = filteredMethods;
      }
    });
    
    return filtered;
  }, [methodTree, methodSearchQuery]);

  // Auto-expand matching items when searching
  React.useEffect(() => {
    if (methodSearchQuery.trim()) {
      const newExpandedClasses = new Set();
      const newExpandedMethods = new Set();
      
      Object.keys(filteredMethodTree).forEach(className => {
        newExpandedClasses.add(className);
        Object.keys(filteredMethodTree[className]).forEach(methodName => {
          newExpandedMethods.add(`${className}::${methodName}`);
        });
      });
      
      setExpandedClasses(newExpandedClasses);
      setExpandedMethods(newExpandedMethods);
    }
  }, [filteredMethodTree, methodSearchQuery]);

  const handleCloseImpactedTestsModal = () => {
    setImpactedTestsModalOpen(false);
    setSelectedImpactedTests([]);
    setTestSearchQuery('');
  };

  /**
   * Open logs dialog for a PR
   */
  const handleOpenLogsDialog = (pr) => {
    setSelectedPRForLogs(pr);
    setLogsDialogOpen(true);
  };

  /**
   * Close logs dialog
   */
  const handleCloseLogsDialog = () => {
    setLogsDialogOpen(false);
    setSelectedPRForLogs(null);
  };

  /**
   * Format log level for display
   */
  const getLogLevelChip = (level) => {
    const config = {
      log: { color: 'default', label: 'LOG' },
      info: { color: 'info', label: 'INFO' },
      warn: { color: 'warning', label: 'WARN' },
      error: { color: 'error', label: 'ERROR' },
      debug: { color: 'default', label: 'DEBUG' }
    };
    
    const { color, label } = config[level] || config.log;
    return (
      <Chip 
        label={label} 
        color={color} 
        size="small" 
        sx={{ 
          minWidth: 50,
          height: 20,
          fontSize: '0.65rem',
          '& .MuiChip-label': {
            px: 0.5,
            py: 0
          }
        }} 
      />
    );
  };

  /**
   * Format log timestamp
   */
  const formatLogTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  // Function to get ADO URL for a work item
  const getAdoUrl = (adoId) => {
    if (!adoId) return null;
    const adoOrg = process.env.REACT_APP_AZURE_DEVOPS_ORG || 'tr-legal.visualstudio.com';
    const projectName = process.env.REACT_APP_AZURE_DEVOPS_PROJECT || 'HighQ Tech';
    const encodedProject = encodeURIComponent(projectName);
    const baseUrl = adoOrg.includes('visualstudio.com')
      ? `https://${adoOrg}`
      : `https://dev.azure.com/${adoOrg}`;
    return `${baseUrl}/${encodedProject}/_workitems/edit/${adoId}`;
  };

  // Function to determine card color based on status
  const getCardColor = (row) => {
    const status = row.status || row.state || 'open';
    return getStatusColor(status);
  };

  // Filter tests based on search query
  const filterTests = (tests, searchQuery) => {
    if (!searchQuery.trim()) {
      return tests;
    }
    
    const query = searchQuery.toLowerCase();
    return tests.filter(test => {
      const testName = (test.testName || '').toLowerCase();
      const testPath = (test.testPath || '').toLowerCase();
      const tags = Array.isArray(test.tags) 
        ? test.tags.map(tag => String(tag).toLowerCase()).join(' ')
        : '';
      
      return testName.includes(query) || 
             testPath.includes(query) || 
             tags.includes(query);
    });
  };

  const renderCodeChangesModal = () => {
    const classNames = Object.keys(filteredMethodTree).sort();
    
    return (
      <Dialog
        open={codeChangesModalOpen}
        onClose={handleCloseCodeChangesModal}
        maxWidth="xl"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            width: '95%',
            maxWidth: '1600px',
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{modalTitle}</Typography>
            <IconButton edge="end" color="inherit" onClick={handleCloseCodeChangesModal} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {/* Show GitHub Code Changes if available */}
          {githubCodeChanges !== null ? (
            <>
              {loadingGithubCodeChanges && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    Loading code changes...
                  </Typography>
                </Box>
              )}
              {githubCodeChangesError && !loadingGithubCodeChanges && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {githubCodeChangesError}
                </Alert>
              )}
              {!loadingGithubCodeChanges && githubCodeChanges && !githubCodeChangesError && (
                <Box>
                  {githubCodeChanges.summary && (
                    <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                      <Typography variant="h6" gutterBottom>Summary</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={3}>
                          <Typography variant="body2" color="text.secondary">Files Changed</Typography>
                          <Typography variant="h6">{githubCodeChanges.summary.filesChanged || 0}</Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="body2" color="text.secondary">Additions</Typography>
                          <Typography variant="h6" color="success.main">+{githubCodeChanges.summary.totalAdditions || 0}</Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="body2" color="text.secondary">Deletions</Typography>
                          <Typography variant="h6" color="error.main">-{githubCodeChanges.summary.totalDeletions || 0}</Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="body2" color="text.secondary">Total Changes</Typography>
                          <Typography variant="h6">{githubCodeChanges.summary.totalChanges || 0}</Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '60vh', overflow: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>File</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                          <TableCell align="right"><strong>Additions</strong></TableCell>
                          <TableCell align="right"><strong>Deletions</strong></TableCell>
                          <TableCell align="right"><strong>Changes</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {githubCodeChanges.fileChanges && githubCodeChanges.fileChanges.length > 0 ? (
                          githubCodeChanges.fileChanges.map((file, index) => (
                            <TableRow key={index}>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                                {file.filename}
                                {file.previousFilename && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    (renamed from: {file.previousFilename})
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={file.status}
                                  size="small"
                                  color={
                                    file.status === 'added' ? 'success' :
                                    file.status === 'removed' ? 'error' :
                                    file.status === 'modified' ? 'warning' :
                                    'default'
                                  }
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Typography color="success.main">+{file.additions || 0}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography color="error.main">-{file.deletions || 0}</Typography>
                              </TableCell>
                              <TableCell align="right">{file.changes || 0}</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              <Typography variant="body2" color="text.secondary">
                                No file changes found.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </>
          ) : (
            /* Show Impacted Methods (existing functionality) */
            <>
              {loadingMethods && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    Loading methods...
                  </Typography>
                </Box>
              )}
              {methodsError && !loadingMethods && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {methodsError}
                </Alert>
              )}
              {!loadingMethods && classNames.length === 0 && !methodsError ? (
                <Typography variant="body1" align="center" sx={{ py: 4 }}>
                  {methodSearchQuery.trim() 
                    ? 'No methods or tests match your search criteria.'
                    : 'No impacted methods found.'}
                </Typography>
              ) : (
            <Box>
              <TextField
                fullWidth
                placeholder="Search by class name, method name, or test name... (Press Enter to search)"
                value={methodSearchInput}
                onChange={(e) => setMethodSearchInput(e.target.value)}
                onKeyPress={handleMethodSearchKeyPress}
                variant="outlined"
                size="small"
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <List component="nav" sx={{ maxHeight: '60vh', overflow: 'auto' }}>
                {classNames.map(className => {
                  const isClassExpanded = expandedClasses.has(className);
                  const methodNames = Object.keys(filteredMethodTree[className]).sort();
                
                return (
                  <React.Fragment key={className}>
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => toggleClass(className)}
                        sx={{
                          backgroundColor: isClassExpanded ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.08)'
                          },
                          py: 0.5
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          {isClassExpanded ? (
                            <ExpandLessIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                          ) : (
                            <ExpandMoreIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                          )}
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                            {className}
                          </Typography>
                        </Box>
                      </ListItemButton>
                    </ListItem>
                    <Collapse in={isClassExpanded} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {methodNames.map(methodName => {
                          const methodKey = `${className}::${methodName}`;
                          const isMethodExpanded = expandedMethods.has(methodKey);
                          const tests = filteredMethodTree[className][methodName];
                          
                          return (
                            <React.Fragment key={methodKey}>
                              <ListItem disablePadding sx={{ pl: 4 }}>
                                <ListItemButton
                                  onClick={() => toggleMethod(methodKey)}
                                  sx={{
                                    backgroundColor: isMethodExpanded ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
                                    '&:hover': {
                                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                    },
                                    py: 0.5
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    {isMethodExpanded ? (
                                      <ExpandLessIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1rem' }} />
                                    ) : (
                                      <ExpandMoreIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1rem' }} />
                                    )}
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                      {methodName}
                                    </Typography>
                                  </Box>
                                </ListItemButton>
                              </ListItem>
                              <Collapse in={isMethodExpanded} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                  {tests.map((testName, testIndex) => (
                                    <ListItem key={`${methodKey}-test-${testIndex}`} disablePadding sx={{ pl: 8, py: 0.25 }}>
                                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                        {testName}
                                      </Typography>
                                    </ListItem>
                                  ))}
                                </List>
                              </Collapse>
                            </React.Fragment>
                          );
                        })}
                      </List>
                    </Collapse>
                  </React.Fragment>
                );
              })}
            </List>
            {methodSearchQuery && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Showing results for "{methodSearchQuery}"
              </Typography>
            )}
            </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCodeChangesModal}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderImpactedTestsModal = () => {
    const filteredTests = filterTests(selectedImpactedTests, testSearchQuery);
    
    return (
      <Dialog
        open={impactedTestsModalOpen}
        onClose={handleCloseImpactedTestsModal}
        maxWidth="xl"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            width: '95%',
            maxWidth: '1600px',
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{modalTitle}</Typography>
            <IconButton edge="end" color="inherit" onClick={handleCloseImpactedTestsModal} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedImpactedTests.length === 0 ? (
            <Typography variant="body1" align="center" sx={{ py: 4 }}>
              No impacted tests found.
            </Typography>
          ) : (
            <Box>
              <TextField
                fullWidth
                placeholder="Search tests by name, path, or tags..."
                value={testSearchQuery}
                onChange={(e) => setTestSearchQuery(e.target.value)}
                variant="outlined"
                size="small"
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '70vh', overflow: 'auto' }}>
                <Table size="small" sx={{ tableLayout: 'auto' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: '300px', maxWidth: '500px' }}><strong>Test Name</strong></TableCell>
                      <TableCell sx={{ minWidth: '250px', maxWidth: '400px' }}><strong>Test Path</strong></TableCell>
                      <TableCell sx={{ minWidth: '200px', maxWidth: '300px' }}><strong>Tags</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          <Typography variant="body2" color="textSecondary">
                            No tests match your search criteria.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredTests.map((test, index) => (
                        <TableRow key={index}>
                          <TableCell sx={{ 
                            whiteSpace: 'normal', 
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            maxWidth: '500px',
                            padding: '12px'
                          }}>
                            {test.testName || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ 
                            whiteSpace: 'normal', 
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            maxWidth: '400px',
                            padding: '12px'
                          }}>
                            {test.testPath || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ 
                            whiteSpace: 'normal', 
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            maxWidth: '300px',
                            padding: '12px'
                          }}>
                            {Array.isArray(test.tags) && test.tags.length > 0
                              ? test.tags.join(', ')
                              : 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {testSearchQuery && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Showing {filteredTests.length} of {selectedImpactedTests.length} tests
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImpactedTestsModal}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={subTab} onChange={(e, v) => setSubTab(v)}>
          <Tab label="Impacted Tests" />
          <Tab label="Daily Execution" />
        </Tabs>
      </Box>

      {subTab === 0 && (
        <>
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" gutterBottom>
            Impacted Tests for PRs
          </Typography>
          <Typography variant="body1" color="textSecondary">
            View impacted tests and methods for pull requests based on code changes between commits
          </Typography>
        </Box>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          {groupedByDate.length > 0 && (
            <FormControlLabel
              control={
                <Switch
                  checked={expandedDateGroups.size === groupedByDate.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setExpandedDateGroups(new Set(groupedByDate.map((g) => g.dateKey)));
                    } else {
                      setExpandedDateGroups(new Set());
                    }
                  }}
                  color="primary"
                />
              }
              label="All expanded"
            />
          )}
          <FormControlLabel
            control={
              <Switch
                checked={showOnlyWithTests}
                onChange={(e) => setShowOnlyWithTests(e.target.checked)}
                color="primary"
              />
            }
            label="Show only PRs with tests"
          />
          <FormControlLabel
            control={
              <Switch
                checked={viewMode === 'card'}
                onChange={(e) => setViewMode(e.target.checked ? 'card' : 'table')}
                color="primary"
              />
            }
            label="Card view"
          />
          {userIsAuthenticated && (
            <Button size="small" variant="outlined" color="secondary" onClick={handleOpenCleanupDialog}>
              Cleanup data
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {viewMode === 'card' ? (
        // Card View
        <Box>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : data.length === 0 ? (
            <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                No recommended tests found.
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ backgroundColor: '#F8F9FA', borderRadius: 2, py: 2, px: 1.5 }}>
              {groupedByDate.map((group) => {
                const isExpanded = expandedDateGroups.has(group.dateKey);
                return (
                  <Box key={group.dateKey} sx={{ mb: 2, '&:last-of-type': { mb: 0 } }}>
                    <Paper
                      elevation={0}
                      onClick={() => toggleDateGroup(group.dateKey)}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: '#FFFFFF',
                        borderRadius: 3,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        transition: 'box-shadow 0.2s ease',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                          backgroundColor: '#FFFFFF'
                        }
                      }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ py: 1.75, px: 2 }}>
                        <Box display="flex" alignItems="center" gap={1.5} flex={1} minWidth={0}>
                          <Typography variant="subtitle1" fontWeight="600" color="text.primary" sx={{ flexShrink: 0 }}>
                            {group.dateLabel}
                          </Typography>
                          <Chip label={`${group.prCount} PR${group.prCount !== 1 ? 's' : ''}`} size="small" sx={{ height: 24 }} variant="outlined" />
                          <Chip label={`${group.uniqueTestCount} unique test${group.uniqueTestCount !== 1 ? 's' : ''}`} size="small" sx={{ height: 24 }} variant="outlined" />
                        </Box>
                        <IconButton size="small" aria-label={isExpanded ? 'Collapse' : 'Expand'} sx={{ color: 'text.secondary', flexShrink: 0 }}>
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Box>
                    </Paper>
                    <Collapse in={isExpanded}>
                      <Grid container spacing={3} sx={{ mt: 0.5 }}>
                        {group.rows.map((row, index) => {
                          const impactedTestsCount = Array.isArray(row.recommendedTests) ? row.recommendedTests.length : 0;
                          const prDetails = row.prDetails;
                          const prNumber = prDetails?.prNumber || row.prNumber;
                          const prAdoId = prDetails?.adoId;
                          const prRepository = prDetails?.repository;
                          const prId = prDetails?.prId;
                          const repository = prRepository || (row.appId ? `highq_${row.appId}` : 'highq_collaborate');
                          const prUrl = prId || (prNumber ? `https://github.com/tr/${repository}/pull/${prNumber}` : null);
                          const adoUrl = getAdoUrl(prAdoId);
                          const cardColor = getCardColor(row);
                          return (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={row._id || index}>
                              <Card
                                sx={{
                                  height: '100%',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  backgroundColor: cardColor,
                                  color: 'white',
                                  transition: 'transform 0.2s, box-shadow 0.2s',
                                  '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: 6
                                  }
                                }}
                              >
                                <CardContent sx={{ flexGrow: 1, pb: 0.5, pt: 1.5, px: 1.5 }}>
                                  {prNumber && prUrl ? (
                                    <Link
                                      href={prUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{
                                        color: 'white',
                                        textDecoration: 'none',
                                        fontWeight: 'bold',
                                        fontSize: '1.1rem',
                                        display: 'block',
                                        mb: 0.5,
                                        '&:hover': { textDecoration: 'underline' }
                                      }}
                                    >
                                      {prNumber}
                                    </Link>
                                  ) : (
                                    <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 'bold', fontSize: '1.1rem' }}>
                                      No PR
                                    </Typography>
                                  )}
                                  <Typography variant="body2" sx={{ mb: 0.5, opacity: 0.9, fontSize: '0.9rem' }}>
                                    Repository: {repository}
                                  </Typography>
                                  {prAdoId && (
                                    <Typography variant="body2" sx={{ mb: 1, opacity: 0.9, fontSize: '0.9rem' }}>
                                      ADO ID: {adoUrl ? (
                                        <Link
                                          href={adoUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          sx={{ color: 'white', textDecoration: 'underline', fontWeight: 'bold', '&:hover': { opacity: 0.8 } }}
                                        >
                                          {prAdoId}
                                        </Link>
                                      ) : (
                                        prAdoId
                                      )}
                                    </Typography>
                                  )}
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                      <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.95rem' }}>
                                        Impacted Tests:
                                      </Typography>
                                      <Chip
                                        label={impactedTestsCount}
                                        size="small"
                                        sx={{
                                          backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                          color: 'white',
                                          fontWeight: 'bold',
                                          fontSize: '0.9rem',
                                          height: '24px',
                                          cursor: 'pointer',
                                          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.5)' }
                                        }}
                                        onClick={() => handleImpactedTestsClick(row.recommendedTests)}
                                      />
                                    </Box>
                                  </Box>
                                </CardContent>
                                <Box sx={{ p: 1, pt: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Chip
                                    label={row.status || row.state || 'open'}
                                    size="small"
                                    sx={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', color: 'white', fontSize: '0.85rem', height: '22px' }}
                                  />
                                </Box>
                              </Card>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Collapse>
                  </Box>
                );
              })}
            </Box>
          )}
          
          {!loading && pagination.totalPages > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2, mt: 3 }}>
              <Stack spacing={2} alignItems="center">
                <Typography variant="body2" color="textSecondary">
                  Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, pagination.total)} of {pagination.total} records
                </Typography>
                <Pagination
                  count={pagination.totalPages}
                  page={page}
                  onChange={(event, value) => setPage(value)}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Stack>
            </Box>
          )}
        </Box>
      ) : (
        // Table View
        <Paper elevation={0} sx={{ width: '100%', maxWidth: '100%', overflowX: 'auto', backgroundColor: '#F8F9FA', borderRadius: 2 }}>
        <TableContainer sx={{ minWidth: 1200 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={orderBy === 'appId'}
                    direction={orderBy === 'appId' ? order : 'asc'}
                    onClick={() => handleRequestSort('appId')}
                  >
                    <strong>Application</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                  <strong>ID</strong>
                </TableCell>
                <TableCell sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                  <strong>Author</strong>
                </TableCell>
                <TableCell sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                  <strong>ADO</strong>
                </TableCell>
                <TableCell sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                  <strong>Commits</strong>
                </TableCell>
                <TableCell sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                  <strong>Files Changed</strong>
                </TableCell>
                <TableCell sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={orderBy === 'impactedTests'}
                    direction={orderBy === 'impactedTests' ? order : 'asc'}
                    onClick={() => handleRequestSort('impactedTests')}
                  >
                    <strong>Impacted Tests</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={orderBy === 'dateTime'}
                    direction={orderBy === 'dateTime' ? order : 'desc'}
                    onClick={() => handleRequestSort('dateTime')}
                  >
                    <strong>Date</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                  <strong>PR State</strong>
                </TableCell>
                <TableCell align="center" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                  <strong>Execution Status</strong>
                </TableCell>
                <TableCell align="center" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                  <strong>Logs</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No recommended tests found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                groupedByDate.flatMap((group) => {
                  const isExpanded = expandedDateGroups.has(group.dateKey);
                  return [
                    <TableRow key={`group-${group.dateKey}`} sx={{ '& > td': { borderBottom: 'none', py: 1, verticalAlign: 'middle' } }}>
                      <TableCell colSpan={11} sx={{ py: 1, px: 0, fontSize: '0.95rem' }}>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          onClick={() => toggleDateGroup(group.dateKey)}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor: '#FFFFFF',
                            borderRadius: 3,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                            py: 1.75,
                            px: 2,
                            transition: 'box-shadow 0.2s ease',
                            '&:hover': {
                              boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                            }
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={1.5} flex={1} minWidth={0}>
                            <Typography variant="subtitle2" fontWeight="600" color="text.primary">
                              {group.dateLabel}
                            </Typography>
                            <Chip label={`${group.prCount} PR${group.prCount !== 1 ? 's' : ''}`} size="small" sx={{ height: 24 }} variant="outlined" />
                            <Chip label={`${group.uniqueTestCount} unique test${group.uniqueTestCount !== 1 ? 's' : ''}`} size="small" sx={{ height: 24 }} variant="outlined" />
                          </Box>
                          <IconButton size="small" aria-label={isExpanded ? 'Collapse' : 'Expand'} sx={{ color: 'text.secondary', flexShrink: 0 }}>
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>,
                    ...(isExpanded
                      ? group.rows.map((row, index) => {
                          const impactedTestsCount = Array.isArray(row.recommendedTests) ? row.recommendedTests.length : 0;
                          const prDetails = row.prDetails;
                          const prNumber = prDetails?.prNumber || row.prNumber;
                          const prTitle = prDetails?.title;
                          const prAuthor = prDetails?.author;
                          const prAdoId = prDetails?.adoId;
                          const prCommit = prDetails?.commit;
                          const prMergeCommit = prDetails?.mergeCommit;
                          const prRepository = prDetails?.repository;
                          const prId = prDetails?.prId;
                          const prState = prDetails?.state;
                          const prStatus = prDetails?.status;
                          const repository = prRepository || (row.appId ? `highq_${row.appId}` : 'highq_collaborate');
                          const githubBaseUrl = `https://github.com/tr/${repository}/commit/`;
                          const currentCommit = prCommit || row.buildVersion || '';
                          const mergeCommit = prMergeCommit || '';
                          const currentCommitUrl = currentCommit ? `${githubBaseUrl}${currentCommit}` : null;
                          const mergeCommitUrl = mergeCommit ? `${githubBaseUrl}${mergeCommit}` : null;
                          const prUrl = prId || (prNumber ? `https://github.com/tr/${repository}/pull/${prNumber}` : null);
                          const adoUrl = getAdoUrl(prAdoId);
                          return (
                            <TableRow key={row._id || index} hover>
                              <TableCell sx={{ fontSize: '0.95rem' }}>{row.appId || 'N/A'}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.95rem' }}>
                                {prNumber && prUrl ? (
                                  <Tooltip title={prTitle || 'No title available'} arrow placement="top">
                                    <Link href={prUrl} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '0.95rem' }}>
                                      {prNumber}
                                    </Link>
                                  </Tooltip>
                                ) : (
                                  'N/A'
                                )}
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.95rem' }}>{prAuthor || 'N/A'}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.95rem' }}>
                                {prAdoId ? (adoUrl ? <Link href={adoUrl} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '0.95rem' }}>{prAdoId}</Link> : prAdoId) : 'N/A'}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'normal', py: 1.5, fontSize: '0.95rem' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', lineHeight: 1.4, color: '#2196f3', fontSize: '0.9rem' }}>
                                    {currentCommitUrl && currentCommit ? (
                                      <Tooltip title={currentCommit} arrow placement="top">
                                        <Link href={currentCommitUrl} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ cursor: 'pointer', fontFamily: 'monospace', textDecoration: 'none', color: '#2196f3', fontSize: '0.9rem' }}>
                                          {getShortSha(currentCommit)}
                                        </Link>
                                      </Tooltip>
                                    ) : (
                                      <span style={{ color: '#9e9e9e' }}>N/A</span>
                                    )}
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontSize: '0.9rem', fontFamily: 'monospace', lineHeight: 1.4, color: '#4caf50' }}>
                                    {mergeCommitUrl && mergeCommit ? (
                                      <Tooltip title={mergeCommit} arrow placement="top">
                                        <Link href={mergeCommitUrl} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ cursor: 'pointer', fontFamily: 'monospace', textDecoration: 'none', color: '#4caf50', fontSize: '0.9rem' }}>
                                          {getShortSha(mergeCommit)}
                                        </Link>
                                      </Tooltip>
                                    ) : (
                                      <span style={{ color: '#9e9e9e' }}>-</span>
                                    )}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.95rem' }}>
                                {prNumber && row.appId ? (
                                  <Link component="button" variant="body2" onClick={() => handleFilesChangedClick(row)} sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    {prDetails?.filesChanged ?? row.githubCodeChanges?.summary?.filesChanged ?? 0}
                                  </Link>
                                ) : (
                                  'N/A'
                                )}
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.95rem' }}>
                                <Link component="button" variant="body2" onClick={() => handleImpactedTestsClick(row.recommendedTests)} sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                  {impactedTestsCount}
                                </Link>
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.95rem', py: 1 }}>
                                {(() => {
                                  const dateValue = prDetails?.lastSyncAt || row.dateTime || row.createdAt;
                                  if (!dateValue) return 'N/A';
                                  const dateObj = new Date(dateValue);
                                  const dateStr = dateObj.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                                  const timeStr = dateObj.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
                                  return (
                                    <Box sx={{ lineHeight: 1.2 }}>
                                      <Typography variant="body2" component="div" sx={{ fontSize: '0.95rem' }}>{dateStr}</Typography>
                                      <Typography variant="body2" component="div" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>{timeStr}</Typography>
                                    </Box>
                                  );
                                })()}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.95rem' }}>
                                <Chip label={prState || row.state || 'N/A'} size="small" sx={{ backgroundColor: getStateColor(prState || row.state || 'N/A'), color: 'white', fontSize: '0.85rem', fontWeight: 'medium' }} />
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.95rem' }}>
                                <Chip label={prStatus || row.status || 'To be Executed'} size="small" sx={{ backgroundColor: getStatusColor(prStatus || row.status || 'To be Executed'), color: 'white', fontSize: '0.85rem', fontWeight: 'medium' }} />
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.95rem' }}>
                                <Tooltip title={(() => {
                                  const logs = prDetails?.logs || row.logs || [];
                                  if (!userIsAuthenticated) return 'Logs available for authenticated users only';
                                  if (!logs || logs.length === 0) return 'No logs available for this pull request';
                                  return 'View Logs';
                                })()}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => { e.stopPropagation(); handleOpenLogsDialog(prDetails || row); }}
                                      disabled={!userIsAuthenticated || !(prDetails?.logs || row.logs || []).length}
                                    >
                                      <DescriptionIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      : [])
                  ];
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!loading && pagination.totalPages > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2, borderTop: 1, borderColor: 'divider' }}>
            <Stack spacing={2} alignItems="center">
              <Typography variant="body2" color="textSecondary">
                Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, pagination.total)} of {pagination.total} records
              </Typography>
              <Pagination
                count={pagination.totalPages}
                page={page}
                onChange={(event, value) => setPage(value)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Stack>
          </Box>
        )}
      </Paper>
      )}

      {renderCodeChangesModal()}
      {renderImpactedTestsModal()}

      {/* Logs Dialog */}
      <Dialog 
        open={logsDialogOpen} 
        onClose={handleCloseLogsDialog}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 2 }}>
          Background Logs for PR #{selectedPRForLogs?.prNumber || selectedPRForLogs?.prDetails?.prNumber}
        </DialogTitle>
        <DialogContent sx={{ p: 1.5, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {selectedPRForLogs && (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              {(() => {
                const logs = selectedPRForLogs.logs || selectedPRForLogs.prDetails?.logs || [];
                return !logs || logs.length === 0 ? (
                  <Typography variant="body2" color="textSecondary" sx={{ p: 1.5 }}>
                    No logs available for this pull request.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                    <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, px: 1 }}>
                      Total logs: {logs.length}
                    </Typography>
                    <TableContainer 
                      component={Paper} 
                      variant="outlined"
                      sx={{ 
                        flex: 1,
                        overflow: 'auto',
                        maxHeight: 'calc(90vh - 120px)'
                      }}
                    >
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell 
                              sx={{ 
                                width: 140, 
                                py: 0.5,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                backgroundColor: 'background.paper'
                              }}
                            >
                              Timestamp
                            </TableCell>
                            <TableCell 
                              sx={{ 
                                width: 70, 
                                py: 0.5,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                backgroundColor: 'background.paper'
                              }}
                            >
                              Level
                            </TableCell>
                            <TableCell 
                              sx={{ 
                                py: 0.5,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                backgroundColor: 'background.paper'
                              }}
                            >
                              Message
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {logs.map((log, index) => (
                            <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                              <TableCell sx={{ 
                                py: 0.5, 
                                px: 1,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    fontFamily: 'monospace',
                                    fontSize: '0.65rem',
                                    lineHeight: 1.2,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: 'block'
                                  }}
                                >
                                  {formatLogTimestamp(log.timestamp)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 0.5, px: 1 }}>
                                {getLogLevelChip(log.level)}
                              </TableCell>
                              <TableCell sx={{ py: 0.5, px: 1 }}>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    fontFamily: 'monospace',
                                    fontSize: '0.65rem',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    lineHeight: 1.3
                                  }}
                                >
                                  {log.message}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button onClick={handleCloseLogsDialog} size="small">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Cleanup data dialog - authenticated users only */}
      <Dialog open={cleanupDialogOpen} onClose={() => !cleanupInProgress && setCleanupDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cleanup data</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Select which data to remove. This cannot be undone.
          </Typography>
          <TextField
            label="Clean up data older than (optional)"
            type="date"
            value={cleanupOlderThan}
            onChange={(e) => setCleanupOlderThan(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{ mb: 2 }}
            helperText="Leave empty to clean all data. When set, only records older than this date are removed."
          />
          {cleanupError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCleanupError(null)}>
              {cleanupError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={cleanupImpactedMethods}
                  onChange={(e) => setCleanupImpactedMethods(e.target.checked)}
                  disabled={cleanupPullRequests || cleanupImpactedTests}
                />
              }
              label="Impacted Methods – delete all method traceability data"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={cleanupImpactedTests}
                  onChange={(e) => setCleanupImpactedTests(e.target.checked)}
                  disabled={cleanupPullRequests}
                />
              }
              label="Impacted Tests – delete all impacted tests and clear PR links (also removes Impacted Methods)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={cleanupPullRequests}
                  onChange={(e) => setCleanupPullRequests(e.target.checked)}
                />
              }
              label="Pull Requests – delete all PRs, Impacted Tests, and Impacted Methods"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button onClick={() => setCleanupDialogOpen(false)} disabled={cleanupInProgress}>
            Cancel
          </Button>
          <Button variant="contained" color="secondary" onClick={handleRunCleanup} disabled={cleanupInProgress}>
            {cleanupInProgress ? 'Cleaning…' : 'Run cleanup'}
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}

      {subTab === 1 && <DailyExecutionTab />}
    </Box>
  );
};

export default RecommendedTestsView;

